/** Lightweight curriculum markdown → HTML */

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inlineMd(s: string): string {
  return escapeHtml(s)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

function calloutClass(text: string): string {
  const t = text.toLowerCase();
  if (t.startsWith("myth:")) return "callout callout--myth";
  if (t.startsWith("fact:")) return "callout callout--fact";
  if (t.startsWith("remember:")) return "callout callout--remember";
  if (t.startsWith("field note:")) return "callout callout--note";
  if (t.startsWith("try this:")) return "callout callout--try";
  if (t.startsWith("honest frame:")) return "callout callout--note";
  return "callout";
}

export function renderBody(md: string): string {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trimEnd();
    if (!line.trim()) {
      i++;
      continue;
    }

    if (/^### /.test(line)) {
      out.push(`<h4 class="lesson-h3">${inlineMd(line.slice(4))}</h4>`);
      i++;
      continue;
    }
    if (/^## /.test(line)) {
      out.push(`<h3 class="lesson-h2">${inlineMd(line.slice(3))}</h3>`);
      i++;
      continue;
    }

    if (line.trim().startsWith(">")) {
      const quotes: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        quotes.push(lines[i].trim().replace(/^>\s?/, ""));
        i++;
      }
      const text = quotes.join(" ");
      out.push(`<aside class="${calloutClass(text)}"><p>${inlineMd(text)}</p></aside>`);
      continue;
    }

    if (line.trim().startsWith("|") && i + 1 < lines.length && lines[i + 1].includes("---")) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        const row = lines[i]
          .trim()
          .replace(/^\|/, "")
          .replace(/\|$/, "")
          .split("|")
          .map((c) => c.trim());
        if (!row.every((c) => /^:?-+:?$/.test(c))) rows.push(row);
        i++;
      }
      out.push('<table class="learn-table">');
      rows.forEach((row, ri) => {
        const tag = ri === 0 ? "th" : "td";
        out.push(
          "<tr>" + row.map((c) => `<${tag}>${inlineMd(c)}</${tag}>`).join("") + "</tr>",
        );
      });
      out.push("</table>");
      continue;
    }

    if (/^[-*] /.test(line.trim())) {
      out.push("<ul>");
      while (i < lines.length && /^[-*] /.test(lines[i].trim())) {
        out.push(`<li>${inlineMd(lines[i].trim().slice(2))}</li>`);
        i++;
      }
      out.push("</ul>");
      continue;
    }

    const block: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith("|") &&
      !lines[i].trim().startsWith(">") &&
      !/^#{1,3} /.test(lines[i].trim()) &&
      !/^[-*] /.test(lines[i].trim())
    ) {
      block.push(lines[i]);
      i++;
    }
    out.push(`<p>${inlineMd(block.join(" "))}</p>`);
  }
  return out.join("");
}

export function readMinutes(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 180));
}
